import {
  hasMinDiscountPercent,
  validateDiscountCategories,
} from 'helpers/partner.helpers';
import { IPartner, IPartnerWorkingHour } from 'types/Partner';
import { validateCNPJ, validateCPF } from 'validations-br';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

const requiredMessage = (fieldName: string) =>
  `${fieldName} não pode ficar em branco`;

const requiredString = (name: string) => {
  const message = `${name} não pode ficar em branco`;
  return Yup.string().typeError(message).required(message);
};

const urlValidation = (value?: string): boolean => {
  if (!value) {
    return true;
  }

  const url =
    value.startsWith('https://') || value.startsWith('http://')
      ? value
      : `https://${value}`;

  try {
    Yup.string().url().validateSync(url);
  } catch (reason) {
    return false;
  }

  return true;
};

const cpfValidation = requiredString('CPF').test(
  'is-cpf',
  'CPF inválido',
  (value: string | undefined) => validateCPF(value as string)
);

const cnpjValidation = requiredString('CNPJ').test(
  'is-cnpj',
  'CNPJ inválido',
  (value: string | undefined) => validateCNPJ(value as string)
);

const startEndValidation = ({ start, end }: { start: string; end: string }) => {
  return (
    start?.length === 4 && end?.length === 4 && end < '2359' && start <= end
  );
};

const validateStartEnd = Yup.string().test(
  'start-end-test',
  'Intervalo inválido',
  (value, context) => {
    const values = context.parent as IPartnerWorkingHour;

    if (!values.open || values.dayLong) {
      return true;
    }

    return startEndValidation(values);
  }
);

const validateIntervalStartEnd = Yup.string()
  .nullable()
  .test('interval-start-end-test', 'Intervalo inválido', (value, context) => {
    const values = context.parent as IPartnerWorkingHour['interval'];

    if (!values?.start && !values?.end) {
      return true;
    }

    return startEndValidation(values);
  });

const workingHourSchema = Yup.object().shape({
  // start: validateStartEnd,
  end: validateStartEnd,
  interval: Yup.object().shape({
    // start: validateIntervalStartEnd,
    end: validateIntervalStartEnd,
  }),
  open: Yup.boolean(),
  dayLong: Yup.boolean(),
});

const socialMediasValidation = Yup.object().shape({
  website: Yup.string().test(
    'website-validation',
    'Site deve ser uma url válida. Ex: "www.meusite.com.br"',
    urlValidation
  ),
  instagram: Yup.string().test(
    'instagram-validation',
    'Instagram deve ser uma url válida. Ex: "instagram.com/minha_pagina"',
    urlValidation
  ),
  facebook: Yup.string().test(
    'facebook-validation',
    'Facebook deve ser uma url válida. Ex: "facebook.com/minha_pagina"',
    urlValidation
  ),
});

const partnerDiscountsValidation = Yup.array().of(
  Yup.object().shape({
    discountValue: Yup.number()
      .required(requiredMessage('Desconto'))
      .typeError(requiredMessage('Desconto'))
      .min(0, 'Mínimo 0%')
      .max(100, 'Máximo 100%'),
    categories: Yup.object()
      .shape({})
      .test(
        'categories-validation',
        requiredMessage('Itens'),
        validateDiscountCategories
      ),
  })
);

export const partnerValidationFromAdmin = yupResolver(
  Yup.object().shape({
    company: requiredString('Nome Fantasia'),
    email: requiredString('E-mail').email('E-mail inválido'),
    workingHoursUpdate: Yup.object().shape({
      friday: workingHourSchema,
      monday: workingHourSchema,
      saturday: workingHourSchema,
      sunday: workingHourSchema,
      thursday: workingHourSchema,
      tuesday: workingHourSchema,
      wednesday: workingHourSchema,
    }),
    socialMedias: socialMediasValidation,
    discountsUpdate: partnerDiscountsValidation,
  })
);

export const partnerUpdateProfileValidation = yupResolver(
  Yup.object().shape({
    types: Yup.object().test(
      'types-test',
      'Tipo de Parceiro não pode ficar em branco',
      (value) => {
        const hasAtLeastOnSelected = Object.values(value).some(
          (item: IPartner['types']) => item
        );

        return hasAtLeastOnSelected;
      }
    ),
    socialReason: requiredString('Razão Social'),
    company: requiredString('Nome Fantasia'),
    cnpj: cnpjValidation,
    cpf: cpfValidation,
    responsible: requiredString('Nome Completo do responsável'),
    mobilePhone: Yup.object().shape({
      number: requiredString('Telefone Celular'),
    }),
    acceptPartnershipTerms: Yup.object().shape({
      isAccepted: Yup.boolean().oneOf(
        [true],
        'Você precisa aceitar os termos acima'
      ),
    }),
    address: Yup.object().shape({
      cep: requiredString('CEP'),
      city: requiredString('Cidade'),
      number: requiredString('Número'),
      neighborhood: requiredString('Bairro'),
      street: requiredString('Logradouro'),
      state: requiredString('Estado'),
    }),
    workingHoursUpdate: Yup.object().shape({
      friday: workingHourSchema,
      monday: workingHourSchema,
      saturday: workingHourSchema,
      sunday: workingHourSchema,
      thursday: workingHourSchema,
      tuesday: workingHourSchema,
      wednesday: workingHourSchema,
    }),
    workingHours: Yup.object().test(
      'working-hours-test',
      'Horário de Funcionamento não pode ficar em branco',
      (value) => {
        const hasAtLeastOneOpen = Object.values(value).some(
          (item: IPartnerWorkingHour) => item.open
        );

        return hasAtLeastOneOpen;
      }
    ),
    email: Yup.string().email('E-mail inválido'),
    socialMedias: socialMediasValidation,
    discounts:  partnerDiscountsValidation
      .min(1)
      .required(),
    discountsUpdate: partnerDiscountsValidation
      .min(1)
      .required(),
  })
);
